import { HTMLAttributeAnchorTarget } from 'react';
import { BoxProps } from '@mui/material';
import { UserRole } from './user-types';
import {
  Account,
  Adjustment,
  Advance,
  Alert,
  Asset,
  Company,
  Loan,
  Note,
  Participation,
  Payment,
  Report,
  Statement,
  TAccountId,
  TAdjustmentId,
  TAdvanceId,
  TAlertId,
  TAssetId,
  TCompanyId,
  TLenderId,
  TLoanId,
  TNoteId,
  TParticipationId,
  TPaymentId,
  TReportId,
  TStatementId,
  TTransactionId,
  TUserId,
  Transaction,
  User,
} from '/src/lib/models';

export * from './account-types';
export * from './adjustment-types';
export * from './advance-types';
export * from './alert-types';
export * from './asset-types';
export * from './authentication-types';
export * from './chart-types';
export * from './company-types';
export * from './document-types';
export * from './headlines-types';
export * from './lender-types';
export * from './loan-types';
export * from './note-types';
export * from './participation-types';
export * from './password-types';
export * from './payment-types';
export * from './report-types';
// export * from './statement-types';
export * from './transaction-types';
export * from './user-types';

/**
 * General
 */

export interface IArrayObject<T> {
  [index: number]: T;
  length: number;
}

export type Nullable<T> = T | null;

export type TNodeEnv = 'development' | 'production';

export type URLString = string;

export type FormErrors = Record<string, string>;

export type PasswordDetails = {
  userId: TUserId;
  currentPassword: string;
  newPassword: string;
  verifyPassword: string;
};

/* Dates */

export type ISODateTimeString = string;

export type DateRange = {
  start?: Date;
  end?: Date;
  currentDate?: Date;
};

export enum DateRangeType {
  Yearly = 'yearly',
  Monthly = 'monthly',
  Weekly = 'weekly',
}

export enum DateRangeAction {
  Next = 'next',
  Previous = 'previous',
}

/* Caching */

export type NameCacheItem = {
  _id: string;
  name: string;
  [key: string]: string;
};

export type NameCache = { [key: string]: NameCacheItem[] };

/* Filters */

export type Filters = {
  companyId?: TCompanyId;
  loanId?: TLoanId;
  lenderId?: TLenderId;
};

export type FilterTags = Record<string, string[]>;

export type Filter<T> = {
  _id?: T;
  name?: string;
  tags?: FilterTags;
  [key: string]: T | string | FilterTags | undefined;
};

/* Addresses */

export type Address = {
  street: string;
  city: string;
  state: Region;
  zip: number;
};

export type Phone = {
  primary: string;
  secondary?: string;
  fax?: string;
};

// export type Totals = Record<string, number | Record<string, number>>;
export type Totals = Record<string, number>;

/**
 * Layout
 */
export type ListHeaderItem = {
  propName: string;
  label: string;
  flex?: number;
  textAlign?: 'left' | 'center' | 'right';
  hide?: boolean;
};

export type ListDataItem = {
  propName: string;
  content?: React.ReactNode;
  type?: 'currency' | 'percent' | 'number';
  flex?: number;
  flexDirection?: BoxProps['flexDirection'];
  justifyContent?: BoxProps['justifyContent'];
  alignItems?: BoxProps['alignItems'];
  textAlign?: 'left' | 'center' | 'right' | string;
  url?: string;
  linkTarget?: HTMLAttributeAnchorTarget;
  icon?: React.ReactNode;
  isNotes?: boolean;
  isButton?: boolean;
  tooltip?: string;
  showNotesPopup?: () => void;
  placeholder?: string;
  className?: string;
  hide?: boolean;
  hideOverflow?: boolean;
  onClick?: () => void;
  label?: string;
};

export type ListContentItem = {
  flex?: number;
  textAlign?: 'left' | 'center' | 'right';
  propName: string;
  label: string;
  content: React.ReactNode;
  url?: string;
  isNotes?: boolean;
};

/**
 * Speed Dial
 */
export type SpeedDialAction = {
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
  className?: string;
};

/**
 * Menus
 */
export type TMenuId = string;

export enum MenuItemType {
  Dropdown = 'dropdown',
  Item = 'item',
}

export type TMenu = {
  isPublic: boolean;
  roles: UserRole[];
  items: TMenuItem[];
  shouldRender: (user?: User) => boolean;
};

export type TMenuItem = {
  title: string;
  link: URLString;
  menuItemType: MenuItemType;
  menuItemClass?: MenuItemType;
  uiRoute: URLString;
  isPublic?: boolean;
  roles?: UserRole[];
  position: number;
  items: TSubMenuItem[];
  open: boolean;
  anchor: Nullable<HTMLElement>;
  shouldRender: (user?: User) => boolean;
};

export type TSubMenuItem = {
  title: string;
  link: URLString;
  uiRoute: URLString;
  isPublic?: boolean;
  roles?: UserRole[];
  position: number;
  shouldRender: (user?: User) => boolean;
};

/**
 * API Requests & Responses
 */

export type TBaseQuery = {
  px?: boolean;
};

export type TBaseRequest = {
  px?: string;
};

export type TBaseResponse<PayloadType> = {
  data: PayloadType;
};

export type TResponseMetadata = {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  first_page: number;
  first_page_url: string;
  last_page_url: string;
  next_page_url: string | null;
  previous_page_url: string | null;
};

export type TListResponse<PayloadType> = {
  meta: TResponseMetadata;
  data: PayloadType;
};

export type TGetResponse<PayloadType> = {
  data: PayloadType;
};

export type TLoaderResponse<PayloadType> = {
  error?: Error;
  data?: PayloadType;
};

export type TCredentialsType = 'include' | 'omit' | 'same-origin';

/**
 * Auth
 */
export type TSession = {
  token: TSessionToken;
  user: User;
};

export type TSessionToken = {
  type: string;
  token: string;
  expires_at: string;
};

/**
 * Storage
 */

export enum StorageKeys {
  SessionToken = 'fuse-financial:sessionToken',
  SessionUser = 'fuse-financial:sessionUser',
  SessionExpiration = 'fuse-financial:sessionExpiration',
  Session = 'fuse-financial:session',
}

/**
 * Enums
 */

export enum DataModelType {
  Account = 'Account',
  Adjustment = 'Adjustment',
  Advance = 'Advance',
  Alert = 'Alert',
  Asset = 'Asset',
  Company = 'Company',
  Lender = 'Investor',
  Loan = 'Deal',
  Note = 'Note',
  Participation = 'Participation',
  Payment = 'Payment',
  Report = 'Report',
  Statement = 'Statement',
  Transaction = 'Transaction',
  User = 'User',
}

export enum DataModelPluralType {
  Account = 'accounts',
  Adjustment = 'adjustments',
  Advance = 'advances',
  Alert = 'alerts',
  Asset = 'assets',
  Company = 'companies',
  Lender = 'investors',
  Loan = 'deals',
  Note = 'notes',
  Participation = 'participations',
  Payment = 'payments',
  Report = 'reports',
  Statement = 'statements',
  Transaction = 'transactions',
  User = 'users',
}

export const dataModelPluralTypes: Record<string, string> = {
  [DataModelType.Account]: DataModelPluralType.Account,
  [DataModelType.Adjustment]: DataModelPluralType.Adjustment,
  [DataModelType.Advance]: DataModelPluralType.Advance,
  [DataModelType.Alert]: DataModelPluralType.Alert,
  [DataModelType.Asset]: DataModelPluralType.Asset,
  [DataModelType.Company]: DataModelPluralType.Company,
  [DataModelType.Lender]: DataModelPluralType.Lender,
  [DataModelType.Loan]: DataModelPluralType.Loan,
  [DataModelType.Note]: DataModelPluralType.Note,
  [DataModelType.Participation]: DataModelPluralType.Participation,
  [DataModelType.Payment]: DataModelPluralType.Payment,
  [DataModelType.Report]: DataModelPluralType.Report,
  [DataModelType.Statement]: DataModelPluralType.Statement,
  [DataModelType.Transaction]: DataModelPluralType.Transaction,
  [DataModelType.User]: DataModelPluralType.User,
};

export type DataModel =
  | Account
  | Adjustment
  | Advance
  | Alert
  | Asset
  | Company
  | Loan
  | Note
  | Participation
  | Payment
  | Report
  | Statement
  | Transaction
  | User;

export type DataModelId =
  | TAccountId
  | TAdjustmentId
  | TAdvanceId
  | TAlertId
  | TAssetId
  | TCompanyId
  | TLoanId
  | TNoteId
  | TParticipationId
  | TPaymentId
  | TReportId
  | TStatementId
  | TTransactionId
  | TUserId;

export enum CRUDMode {
  Create = 'create',
  Edit = 'edit',
  Remove = 'remove',
  Read = 'read',
  View = 'view',
}

export enum Region {
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
}
